import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import { toast } from 'react-toastify';
// Components
import ConfirmationModal from './ConfirmationModal';
// API
import AvailabilityService from 'services/AvailabilityService';

/**
 * Online / offline switch for automatic assignment. The florist is taken from the
 * token, so the control only needs to read and flip the flag.
 */
const ShiftToggle = () => {
  const dispatch = useDispatch();
  const [isOnline, setIsOnline] = useState(false);
  const [changedAt, setChangedAt] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSupported, setIsSupported] = useState(true);
  const [pendingRelease, setPendingRelease] = useState(null);

  const applyAvailability = (data) => {
    setIsOnline(Boolean(data?.isOnline));
    setChangedAt(data?.changedAt ?? null);
  };

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const res = await AvailabilityService.getAvailability();
        applyAvailability(res?.data);
      } catch (err) {
        // Only hide it for users the backend rejects outright ("not a florist"). A 404
        // just means the availability endpoint is not deployed yet - keep the control.
        const code = err?.response?.status;
        if (code === 400 || code === 403) {
          setIsSupported(false);
        }
      }
    };
    fetchAvailability();
  }, []);

  const updateAvailability = async (online) => {
    setIsLoading(true);
    try {
      const res = await AvailabilityService.setAvailability(online);
      applyAvailability(res?.data);
      // Going on shift pulls orders from the queue, going off releases the unstarted
      // ones - either way the lists on screen are stale now.
      dispatch({ type: 'REFRESH_ORDERS_LIST' });
      toast.success(online ? 'You are on shift' : 'You are off shift');
    } catch (err) {
      toast.error(err?.response?.data?.message ?? 'Could not change your shift status');
    } finally {
      setIsLoading(false);
      setPendingRelease(null);
    }
  };

  const handleToggle = async () => {
    if (isLoading) {
      return;
    }
    if (!isOnline) {
      updateAvailability(true);
      return;
    }
    // Going offline releases every order the florist has not started yet - check first.
    setIsLoading(true);
    try {
      const res = await AvailabilityService.getAvailability();
      const notStarted = res?.data?.notStartedOrders ?? [];
      setIsLoading(false);
      if (notStarted.length > 0) {
        setPendingRelease(notStarted);
        return;
      }
    } catch (err) {
      setIsLoading(false);
    }
    updateAvailability(false);
  };

  if (!isSupported) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className={`shift-toggle${isOnline ? ' online' : ''}`}
        onClick={handleToggle}
        disabled={isLoading}
      >
        <span className="shift-dot" />
        <span className="shift-text">
          <span className="shift-label">{isOnline ? 'On shift' : 'Off shift'}</span>
          {isOnline && changedAt && (
            <span className="shift-sub">since {moment(changedAt).format('HH:mm')}</span>
          )}
        </span>
        <span className="shift-switch">
          <span className="shift-knob" />
        </span>
      </button>

      {pendingRelease && (
        <ConfirmationModal
          isOpen={Boolean(pendingRelease)}
          setIsOpen={() => setPendingRelease(null)}
          message={`Going off shift releases ${pendingRelease.length} order${
            pendingRelease.length > 1 ? 's' : ''
          } you have not started yet back to the queue. Orders you already started stay with you.`}
          handleCancelClick={() => setPendingRelease(null)}
          handleProceedClick={() => updateAvailability(false)}
          proceedBtnName="Go off shift"
          isDisabledBtn={isLoading}
        />
      )}
    </>
  );
};

export default ShiftToggle;
